'use client'

/* Adapted from Beautiful UI (MIT): https://www.beautifului.dev/r/prompt-bar.json
 * Reduced to a labelled composer: no @-sources, slash commands, model picker or glimm sweep. */

import { useLayoutEffect, useRef } from 'react'
import { cn } from '../../lib/utils'

export interface PromptBarProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder?: string
  disabled?: boolean
  /** A reply is in flight: the composer stays editable but cannot submit. */
  busy?: boolean
  autoFocus?: boolean
  /** Character cap; unlimited when omitted. */
  maxLength?: number
}

const MAX_HEIGHT = 160

/** Auto-growing chat composer. Enter submits, Shift+Enter inserts a newline, IME composition never submits. */
export function PromptBar({ value, onChange, onSubmit, placeholder = 'Ask anything', disabled, busy, autoFocus, maxLength }: PromptBarProps) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const canSend = !disabled && !busy && value.trim().length > 0

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`
  }, [value])

  const expanded = value.includes('\n') || value.length > 56

  return (
    <div
      className={cn(
        'relative isolate flex flex-col overflow-hidden border border-[var(--line)] bg-[var(--surface)] p-2 transition-[border-color,border-radius,box-shadow] duration-150 focus-within:border-[var(--l3-violet)]/50 focus-within:ring-4 focus-within:ring-[var(--l3-violet)]/10',
        expanded ? 'rounded-[22px]' : 'rounded-2xl',
      )}
    >
      <div className={cn('grid items-end gap-x-1 gap-y-1.5', expanded ? 'grid-cols-[minmax(0,1fr)]' : 'grid-cols-[minmax(0,1fr)_32px]')}>
        <textarea
          ref={ref}
          rows={1}
          maxLength={maxLength}
          aria-label="Message"
          autoFocus={autoFocus}
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
            e.preventDefault()
            if (canSend) onSubmit()
          }}
          className="col-start-1 row-start-1 min-h-8 w-full min-w-0 resize-none bg-transparent px-2 py-[6px] text-[15px] leading-5 text-[var(--ink)] outline-none [overflow-wrap:anywhere] placeholder:text-[var(--ink-3)] disabled:opacity-60"
        />
        <button
          type="button"
          aria-label="Send"
          disabled={!canSend}
          onClick={onSubmit}
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-full transition-[background-color,color,transform] duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--l3-violet)] enabled:active:scale-[0.94]',
            expanded ? 'col-start-1 row-start-2 justify-self-end' : 'col-start-2 row-start-1',
          )}
          style={{
            background: canSend ? 'var(--l3-violet)' : 'transparent',
            color: canSend ? 'white' : 'var(--ink-3)',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </button>
      </div>
    </div>
  )
}
